'use client';

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { cn } from '@edgevanta/ui/lib/utils';

import { NAVIGATION_START_EVENT } from '@/lib/navigation-progress-events';

// A discrete, global navigation progress line pinned to the very top of the
// viewport. It exists purely for feedback: some routes (e.g. a contractor's
// profile) do real work before they paint, and with prefetch off the fetch only
// starts on click — so without this the previous page just sits there and the
// click feels ignored.
//
// The start signal is Next's official router-transition hook
// (onRouterTransitionStart in instrumentation-client.ts), bridged to here via a
// window event. That covers <Link> clicks and router.push/replace/back alike and
// fires before the destination renders — no History-API patching, no click
// interception. Completion is detected when usePathname/useSearchParams change,
// i.e. the new route has committed.

type Phase = 'idle' | 'loading' | 'finishing';

function NavigationProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Re-renders on every committed navigation; used to detect arrival.
  const routeKey = `${pathname}?${searchParams.toString()}`;
  const lastRoute = useRef(routeKey);

  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);

  // Loading timers live in refs (not a phase-keyed effect) so a navigation that
  // begins while one is still in flight restarts the trickle and safety timer.
  const trickle = useRef<number | null>(null);
  const safety = useRef<number | null>(null);
  const clearLoadingTimers = useCallback(() => {
    if (trickle.current !== null) {
      window.clearInterval(trickle.current);
      trickle.current = null;
    }
    if (safety.current !== null) {
      window.clearTimeout(safety.current);
      safety.current = null;
    }
  }, []);

  // Start: fired by onRouterTransitionStart before the destination renders, so a
  // slow route still gives immediate feedback. Restart cleanly each time.
  //
  // onRouterTransitionStart runs inside React's navigation startTransition, so a
  // setState made synchronously here is tagged as a transition (low-priority)
  // update and won't paint until the transition resolves — i.e. when the new
  // route is already ready, which is exactly too late for "click received"
  // feedback. Deferring to a microtask escapes the transition scope so the
  // update lands as urgent and the bar paints immediately on click.
  useEffect(() => {
    const onStart = () => {
      queueMicrotask(() => {
        clearLoadingTimers();
        setPhase('loading');
        setProgress(8);
        // Ease toward — but never reach — the end while we wait. Each tick is an
        // urgent update (see above), so a faster cadence preempts the in-flight
        // navigation transition once a render costs more than the interval.
        trickle.current = window.setInterval(() => {
          setProgress((p) =>
            p >= 92 ? p : Math.min(92, p + Math.max(3, (92 - p) * 0.35)),
          );
        }, 1000);
        // Safety net: never leave the bar stuck if a navigation is abandoned.
        safety.current = window.setTimeout(() => setPhase('finishing'), 12000);
      });
    };
    window.addEventListener(NAVIGATION_START_EVENT, onStart);
    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, onStart);
      clearLoadingTimers();
    };
  }, [clearLoadingTimers]);

  // Arrival: the destination route has committed/painted.
  useEffect(() => {
    if (routeKey === lastRoute.current) {
      return;
    }
    lastRoute.current = routeKey;
    setPhase((prev) => (prev === 'loading' ? 'finishing' : prev));
  }, [routeKey]);

  // Snap to full then fade, and reset width once invisible. Loading is driven by
  // the start handler above; this only handles the tail of the animation.
  useEffect(() => {
    if (phase === 'finishing') {
      clearLoadingTimers();
      setProgress(100);
      const done = window.setTimeout(() => setPhase('idle'), 320);
      return () => window.clearTimeout(done);
    }
    if (phase === 'idle') {
      const reset = window.setTimeout(() => setProgress(0), 240);
      return () => window.clearTimeout(reset);
    }
  }, [phase, clearLoadingTimers]);

  const visible = phase !== 'idle';

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[200] h-0.5"
      // Radix's aria-hidden helper marks everything outside an open overlay
      // with `data-aria-hidden`, and it does so by mutating the DOM directly.
      // When a dialog is already open as this bar re-renders, the attribute is
      // on the element but never in React's tree, so hydration reports a
      // mismatch on markup nobody wrote. Suppressed here only: the bar is
      // presentational and has no server-rendered content to protect.
      suppressHydrationWarning
    >
      <div
        className={cn(
          'relative h-full bg-primary shadow-[0_0_10px_0] shadow-primary/50 transition-[width,opacity] ease-out',
          // Spans a full trickle tick while loading so the bar reads as motion
          // rather than a step; the tail (snap to 100%, fade) stays snappy.
          phase === 'loading' ? 'duration-1000' : 'duration-300',
        )}
        style={{ width: `${progress}%`, opacity: visible ? 1 : 0 }}
      >
        {/* Leading comet — a soft glow at the bar's head for a sense of motion. */}
        <span className="absolute right-0 top-0 h-full w-24 translate-x-1.5 bg-gradient-to-r from-transparent to-primary blur-[3px]" />
      </div>
    </div>
  );
}

// useSearchParams must sit under a Suspense boundary so the root layout isn't
// forced into client rendering.
export default function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <NavigationProgressBar />
    </Suspense>
  );
}
